/** WIZARD "NUEVO PRESUPUESTO" — Fase 7.2. Orquesta lo existente: proyectos, plantillas, recetas y PDF.
 *  VER → TOCAR → ELEGIR → CONFIRMAR. No crea motores nuevos. */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CrearDesdeFoto } from './CrearDesdeFoto';

const PASOS = ['Cliente', 'Crear', 'Producto', 'Plantilla', 'Medidas', 'Accesorios', 'Calcular', 'PDF'];
const PRODUCTOS = [
  { v: 'pabm', ico: '🚿', label: 'Puerta de ducha', desc: 'Puerta de baño de vidrio templado (abatible)' },
  { v: 'vtf', ico: '🪟', label: 'Vidrio fijo de ducha', desc: 'Paño fijo de vidrio templado' },
  { v: 'ventanas', ico: '🏠', label: 'Ventana', desc: 'PVC, aluminio o vidrio — con diseño 2D' },
  { v: 'puertas', ico: '🚪', label: 'Puerta', desc: 'PVC, aluminio, madera o vidrio' },
];

export default function WizardPresupuesto({ onClose }) {
  const navigate = useNavigate();
  const [paso, setPaso] = useState(0);
  const [modo, setModo] = useState(null); // plantilla | cero | foto
  const [showFotoIA, setShowFotoIA] = useState(false);
  const [d, setD] = useState({ cliente: '', proyecto: '', familia: '', plantilla: null, ancho: '', alto: '', cantidad: 1 });
  const [plantillas, setPlantillas] = useState([]);
  const [calc, setCalc] = useState(null); // {proyecto, elemento, total} | {pendiente}
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/sa/plantillas').then((r) => setPlantillas(r.data.plantillas || [])).catch(() => {}); }, []);

  const puedeContinuar = [
    d.cliente.trim().length > 1,
    !!modo,
    !!d.familia,
    true,
    Number(d.ancho) > 0 && Number(d.alto) > 0,
    true, true, true,
  ][paso];

  const sig = () => {
    if (paso === 1 && modo === 'foto') { setShowFotoIA(true); return; }
    if (paso === 2 && modo === 'cero') { setD((x) => ({ ...x, plantilla: null })); setPaso(4); return; }
    if (paso === 5) { calcular(); return; }
    setPaso((p) => Math.min(p + 1, 7));
  };
  const atras = () => {
    if (paso === 4 && modo === 'cero') { setPaso(2); return; }
    setPaso((p) => Math.max(0, p - 1));
  };

  const calcular = async () => {
    setLoading(true);
    setCalc(null);
    try {
      const pr = (await api.post('/sa/proyectos', { nombre: d.proyecto.trim() || `Proyecto ${d.cliente.trim()}`, cliente_nombre: d.cliente.trim() })).data;
      const body = {
        familia: d.familia,
        nombre: d.plantilla?.nombre || PRODUCTOS.find((x) => x.v === d.familia)?.label || '',
        medidas: { ancho_m: Number(d.ancho), alto_m: Number(d.alto) }, cantidad: Number(d.cantidad) || 1,
        variables: d.plantilla?.variables || {},
      };
      if (['ventana2d', 'composicion'].includes(d.plantilla?.diseno?.tipo)) body.diseno = { ...d.plantilla.diseno };
      const el = (await api.post(`/sa/proyectos/${pr.id}/elementos`, body)).data;
      const full = (await api.get(`/sa/proyectos/${pr.id}`)).data;
      setCalc({ proyecto: full, elemento: el, total: full?.totales?.total || 0 });
      setPaso(6);
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = typeof det === 'string' ? det
        : Array.isArray(det) ? det.map((x) => x.msg || '').join(' · ')
        : 'Faltan datos por definir (revisa medidas y opciones)';
      setCalc({ pendiente: msg });
      setPaso(6);
    } finally { setLoading(false); }
  };

  const pdf = async () => {
    try {
      const r = await api.get(`/sa/proyectos/${calc.proyecto.id}/pdf`, { params: { disenos: 1 }, responseType: 'blob', timeout: 60000 });
      const u = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(u, '_blank');
    } catch { toast.error('No se pudo generar el PDF'); }
  };

  const plantillasFam = plantillas.filter((t) => t.familia === d.familia);
  const BIG = 'w-full text-left border-2 rounded-2xl p-4 transition-colors';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-4" data-testid="wizard-presupuesto">
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[95vh] flex flex-col overflow-hidden">
        <div className="px-4 pt-3 pb-2 border-b border-[var(--border)]">
          <div className="flex items-center justify-between">
            <p className="text-base font-black text-[var(--text)]">Nuevo presupuesto</p>
            <button onClick={onClose} className="text-[var(--text-muted)] font-black px-2" data-testid="wizard-cerrar">✕</button>
          </div>
          <div className="flex gap-1 mt-2" data-testid="wizard-progreso">
            {PASOS.map((p, i) => (
              <div key={p} className="flex-1">
                <div className={`h-1.5 rounded-full ${i <= paso ? 'bg-[#0EA5E9]' : 'bg-[var(--border)]'}`} />
                <p className={`text-[8px] mt-0.5 text-center font-bold ${i === paso ? 'text-[#0EA5E9]' : 'text-[var(--text-muted)]'}`}>{p}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {paso === 0 && (
            <div className="space-y-3" data-testid="wizard-paso-cliente">
              <p className="text-sm font-bold text-[var(--text)]">¿Para quién es el presupuesto?</p>
              <Input autoFocus placeholder="Nombre del cliente *" value={d.cliente} onChange={(e) => setD({ ...d, cliente: e.target.value })} className="h-12 text-base" data-testid="wizard-cliente" />
              <Input placeholder="Nombre del proyecto (opcional, ej. Baño principal)" value={d.proyecto} onChange={(e) => setD({ ...d, proyecto: e.target.value })} className="h-12 text-base" data-testid="wizard-proyecto" />
            </div>
          )}

          {paso === 1 && (
            <div className="space-y-2" data-testid="wizard-paso-modo">
              <p className="text-sm font-bold text-[var(--text)]">¿Cómo quieres crear el presupuesto?</p>
              {[
                ['plantilla', '📐 Desde plantilla', 'Elige un diseño de la biblioteca visual y solo ajusta medidas.'],
                ['cero', '✏️ Desde cero', 'Define producto y medidas; personaliza el diseño después.'],
                ['foto', '📷 Desde foto o plano', 'Sube JPG, PNG o PDF — la IA lo lee, te muestra "esto entendí" y solo pregunta lo dudoso.'],
              ].map(([v, tit, desc]) => (
                <button key={v} type="button" onClick={() => setModo(v)} data-testid={`wizard-modo-${v}`}
                  className={`${BIG} ${modo === v ? 'border-[#0EA5E9] bg-[#0EA5E9]/10' : 'border-[var(--border)] hover:border-[#0EA5E9]/60'}`}>
                  <p className="text-base font-black text-[var(--text)]">{tit}</p>
                  <p className="text-xs text-[var(--text-muted)]">{desc}</p>
                </button>
              ))}
            </div>
          )}

          {paso === 2 && (
            <div className="space-y-2" data-testid="wizard-paso-producto">
              <p className="text-sm font-bold text-[var(--text)]">¿Qué vas a cotizar?</p>
              {PRODUCTOS.map((pr) => (
                <button key={pr.v} type="button" onClick={() => setD({ ...d, familia: pr.v, plantilla: null })} data-testid={`wizard-prod-${pr.v}`}
                  className={`${BIG} ${d.familia === pr.v ? 'border-[#0EA5E9] bg-[#0EA5E9]/10' : 'border-[var(--border)] hover:border-[#0EA5E9]/60'}`}>
                  <p className="text-base font-black text-[var(--text)]">{pr.ico} {pr.label}</p>
                  <p className="text-xs text-[var(--text-muted)]">{pr.desc}</p>
                </button>
              ))}
            </div>
          )}

          {paso === 3 && (
            <div className="space-y-2" data-testid="wizard-paso-plantilla">
              <p className="text-sm font-bold text-[var(--text)]">Elige una plantilla o empieza desde cero</p>
              <button type="button" onClick={() => setD({ ...d, plantilla: null })} data-testid="wizard-sin-plantilla"
                className={`${BIG} ${!d.plantilla ? 'border-[#0EA5E9] bg-[#0EA5E9]/10' : 'border-[var(--border)]'}`}>
                <p className="text-sm font-black text-[var(--text)]">✏️ Desde cero</p>
                <p className="text-xs text-[var(--text-muted)]">Solo medidas — podrás personalizar el diseño después.</p>
              </button>
              {plantillasFam.map((t) => (
                <button key={t.id} type="button" onClick={() => setD({ ...d, plantilla: t })} data-testid={`wizard-plantilla-${t.id}`}
                  className={`${BIG} flex gap-3 items-center ${d.plantilla?.id === t.id ? 'border-[#0EA5E9] bg-[#0EA5E9]/10' : 'border-[var(--border)]'}`}>
                  {t.imagen_path
                    ? <img src={`${api.defaults.baseURL}/sa/catalogo/imagen/${t.imagen_path}`} alt="" className="w-16 h-14 object-contain bg-white rounded border border-[var(--border)]" />
                    : <span className="text-3xl">📐</span>}
                  <span>
                    <p className="text-sm font-black text-[var(--text)]">{t.nombre}</p>
                    {t.descripcion && <p className="text-xs text-[var(--text-muted)]">{t.descripcion}</p>}
                    <p className="text-[10px] font-bold text-[#0EA5E9]">Usar esta plantilla →</p>
                  </span>
                </button>
              ))}
              {plantillasFam.length === 0 && <p className="text-xs text-[var(--text-muted)] italic">No hay plantillas para este producto todavía.</p>}
            </div>
          )}

          {paso === 4 && (
            <div className="space-y-3" data-testid="wizard-paso-medidas">
              <p className="text-sm font-bold text-[var(--text)]">Medidas</p>
              {d.plantilla && (
                <div className="flex items-center gap-2 border border-[#0EA5E9]/40 bg-[#0EA5E9]/5 rounded-xl p-2" data-testid="wizard-plantilla-preview">
                  {d.plantilla.imagen_path
                    ? <img src={`${api.defaults.baseURL}/sa/catalogo/imagen/${d.plantilla.imagen_path}`} alt="" className="w-20 h-16 object-contain bg-white rounded" />
                    : <span className="text-3xl">📐</span>}
                  <p className="text-xs text-[var(--text)]"><b>{d.plantilla.nombre}</b> — solo ajusta las medidas; el diseño ya está armado.</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-bold text-[var(--text-muted)]">Ancho (m) *</label>
                  <Input autoFocus type="number" step="0.01" inputMode="decimal" placeholder="1.50" value={d.ancho} onChange={(e) => setD({ ...d, ancho: e.target.value })} className="h-14 text-xl text-center font-black" data-testid="wizard-ancho" /></div>
                <div><label className="text-xs font-bold text-[var(--text-muted)]">Alto (m) *</label>
                  <Input type="number" step="0.01" inputMode="decimal" placeholder="1.90" value={d.alto} onChange={(e) => setD({ ...d, alto: e.target.value })} className="h-14 text-xl text-center font-black" data-testid="wizard-alto" /></div>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs font-bold text-[var(--text-muted)]">Cantidad</label>
                <Button type="button" variant="outline" className="h-11 w-11 text-lg" onClick={() => setD({ ...d, cantidad: Math.max(1, Number(d.cantidad) - 1) })} data-testid="wizard-cant-menos">−</Button>
                <span className="text-xl font-black w-8 text-center" data-testid="wizard-cantidad">{d.cantidad}</span>
                <Button type="button" variant="outline" className="h-11 w-11 text-lg" onClick={() => setD({ ...d, cantidad: Number(d.cantidad) + 1 })} data-testid="wizard-cant-mas">+</Button>
              </div>
            </div>
          )}

          {paso === 5 && (
            <div className="space-y-2" data-testid="wizard-paso-accesorios">
              <p className="text-sm font-bold text-[var(--text)]">Accesorios y uniones</p>
              <div className="border border-[var(--border)] rounded-xl p-3 text-sm text-[var(--text)]">
                <p>🔩 <b>Herrajes</b>: {['pabm', 'vtf'].includes(d.familia) ? 'la receta los agrega automáticamente; podrás ajustarlos tocando el dibujo.' : 'podrás insertarlos tocando el diseño.'}</p>
                <p className="mt-1">↔ <b>Uniones</b>: se detectan automáticamente con las reglas técnicas (ángulos y holguras vigentes).</p>
                <p className="mt-1 text-xs text-[var(--text-muted)]">Después de calcular, toca "Editar" para configurar módulo, puerta, unión o herraje con paneles touch.</p>
              </div>
            </div>
          )}

          {paso === 6 && (
            <div className="space-y-3 text-center" data-testid="wizard-paso-calcular">
              {loading ? <p className="text-sm font-bold py-8">Calculando…</p> : calc?.pendiente ? (
                <div className="border border-amber-500/60 bg-amber-500/10 rounded-xl p-4" data-testid="wizard-pendiente">
                  <p className="text-sm font-black text-amber-600">Falta definir:</p>
                  <p className="text-sm text-[var(--text)] mt-1">{calc.pendiente}</p>
                  <Button variant="outline" className="mt-3 h-11" onClick={() => setPaso(4)}>← Corregir medidas</Button>
                </div>
              ) : calc ? (
                <div className="space-y-2" data-testid="wizard-resultado">
                  <p className="text-xs font-bold text-[var(--text-muted)]">Total del presupuesto</p>
                  <p className="text-4xl font-black text-[#0EA5E9]" data-testid="wizard-total">Q{Number(calc.total).toFixed(2)}</p>
                  <p className="text-sm text-[var(--text)]">{calc.proyecto.numero} · {d.cliente} · {PRODUCTOS.find((x) => x.v === d.familia)?.label} {d.ancho}×{d.alto} m ×{d.cantidad}</p>
                  {(calc.elemento?.regla_tecnica_pendiente || !Number(calc.total)) && (
                    <div className="border border-amber-500/60 bg-amber-500/10 rounded-xl p-3 text-left" data-testid="wizard-aviso-receta">
                      <p className="text-xs font-black text-amber-600">⚠ Falta completar la receta técnica y los costos</p>
                      <p className="text-[11px] text-[var(--text)] mt-0.5">El total aún no refleja materiales. Toca <b>"Editar diseño"</b> para configurar módulos, materiales y costos antes de generar el PDF.</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <Button variant="outline" className="h-12" onClick={() => { onClose(); navigate(`/presupuestos-sa/${calc.proyecto.id}`); }} data-testid="wizard-editar">✏️ Editar diseño</Button>
                    <Button className="h-12 bg-red-600 hover:bg-red-700 text-white" onClick={() => { pdf(); setPaso(7); }} data-testid="wizard-pdf">📄 Generar PDF</Button>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {paso === 7 && calc?.proyecto && (
            <div className="space-y-3 text-center py-4" data-testid="wizard-paso-pdf">
              <p className="text-3xl">✅</p>
              <p className="text-base font-black text-[var(--text)]">Presupuesto {calc.proyecto.numero} listo</p>
              <p className="text-sm text-[var(--text-muted)]">El PDF se abrió en otra pestaña. Puedes seguir editando el diseño cuando quieras.</p>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="h-12" onClick={() => { onClose(); navigate(`/presupuestos-sa/${calc.proyecto.id}`); }}>Abrir presupuesto</Button>
                <Button className="h-12 bg-[#0EA5E9] text-white" onClick={onClose} data-testid="wizard-fin">Terminar</Button>
              </div>
            </div>
          )}
        </div>

        {paso < 6 && (
          <div className="p-3 border-t border-[var(--border)] flex gap-2">
            <Button variant="outline" className="h-12 flex-1" disabled={paso === 0} onClick={atras} data-testid="wizard-atras">← Atrás</Button>
            <Button className="h-12 flex-[2] bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-black" disabled={!puedeContinuar || loading}
              onClick={sig} data-testid="wizard-continuar">
              {paso === 5 ? (loading ? 'Calculando…' : '🧮 CALCULAR PRESUPUESTO') : paso === 1 && modo === 'foto' ? '📷 Adjuntar foto o plano →' : 'Continuar →'}
            </Button>
          </div>
        )}
      </div>
      {showFotoIA && <CrearDesdeFoto open onClose={() => { setShowFotoIA(false); onClose(); }} />}
    </div>
  );
}
